import React, { ReactNode, RefObject, useRef, useState } from "react";
import { Placement, PositioningStrategy } from "@popperjs/core";
import {
	BasePopover,
	DataComponent,
	DataObserveKey,
	VisualComponent,
	FocusableComponent,
	Icon,
	IconCaret,
	IconClose,
	SrOnly,
	Button,
	ButtonProps,
	Divider,
	useUniqueId,
	useTranslations,
	cn,
	Tooltip,
	TooltipProps,
} from "@siteimprove/fancylib";
import {
	combinedRefs,
	dataObserveKeyDiscriminator,
	isMouseEventKeyboard,
	stopEvent,
} from "../../../utils/shorthands";
import { isTargetRef, setFocus, Trigger } from "../../../utils/dom-utils";
import { useDidUpdate } from "../../../utils/hooks";
import { useOnClickOutside } from "../../../utils/hooks";
import { isBlurNavigatingOut, KeyCode } from "../../../utils/keyboard-nav-utils";
import * as scss from "./popover.scss";

export type PopoverProps = {
	/** content for popover element over dropdown */
	popoverContent: (
		id: string,
		/**
		 * Should be assigned to the first focusable element in the popover.
		 *  This element will receive focus when the dropdown opens
		 */
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		firstFocusableRef: React.RefObject<any>,
		openState: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void; close: () => void },
		trigger?: Trigger
	) => ReactNode;
	/** Text and other content for dropdown menu */
	buttonContent: ReactNode;
	/** Props to affect the styling of the dropdown button */
	buttonProps?: DropdownButtonProps;
	/** If set to true, popover label may be set to the id, if isOpen is also true*/
	labelledByPopover?: boolean;
	/** Preferred placement for the popover */
	placement?: Placement;
	/** Allowed placements for the popover when using an "auto" value for the "placement" prop */
	allowedAutoPlacements?: Placement[];
	/** Position popover using fixed or absolute */
	strategy?: PositioningStrategy;
	/** reference sent to basePopover element */
	buttonRef?: RefObject<HTMLButtonElement>;
	/** If set to true the dropdown will open automatically */
	startOpen?: boolean;
	/** Is the dropdown in an invalid state */
	invalid?: boolean;
	/** Classname for container of both dropdown and popover */
	containerClassName?: string;
	/** If set to true, chevron icon will be hidden */
	hideChevron?: boolean;
	/** If true, the default minimum width won't be set */
	noMinWidth?: boolean;
	/** If true, the default maximum width won't be set */
	noMaxWidth?: boolean;
	/** Makes the component non-interactive */
	disabled?: boolean;
	/** Describe what happens if the button is clicked (for icon only buttons) */
	"aria-label"?: string;
	/** What type of content is expanded. Should only be set if the content has the corrosponding 'role' */
	"aria-haspopup"?: "menu" | "listbox" | "dialog";
	/** Callback for onClose events */
	onClose?: () => void;
	/** Callback for clearing the dropdown (displays clear button when provided) */
	onClear?: () => void;
	/** Tooltip text */
	tooltip?: TooltipProps["content"];
	/** Tooltip placement */
	tooltipPlacement?: TooltipProps["placement"];
	/** Key to control updates to the placement of the popover. Provide a different value when an update is desired */
	updateKeys?: React.DependencyList;
} & DataComponent &
	DataObserveKey &
	VisualComponent &
	FocusableComponent;

export function Popover(props: PopoverProps): JSX.Element {
	const {
		onClose,
		popoverContent,
		buttonContent,
		labelledByPopover,
		buttonProps,
		placement,
		allowedAutoPlacements,
		buttonRef = React.createRef<HTMLButtonElement>(),
		startOpen,
		containerClassName,
		hideChevron,
		noMinWidth,
		noMaxWidth,
		disabled,
		"aria-label": ariaLabel,
		"aria-haspopup": ariaHaspopup,
		className,
		style,
		tabIndex,
		invalid,
		onKeyDown,
		onMouseDown,
		onFocus,
		onBlur,
		onClear,
		onMouseEnter,
		onMouseLeave,
		strategy,
		tooltip,
		tooltipPlacement,
		updateKeys = [],
	} = props;

	const [isOpen, setIsOpen] = useState(startOpen || false);
	const close = React.useCallback(() => setIsOpen(false), [setIsOpen]);

	const firstFocusableInPopoverRef = React.createRef<HTMLElement>();

	const id = useUniqueId("dropdown-box");

	const i18n = useTranslations();

	//Keeps track of what triggered the toggle
	const whatTriggered = useRef(Trigger.Mouse);
	const handleToggle = (open: boolean, trigger: Trigger) => {
		whatTriggered.current = trigger;
		setIsOpen(open);
		if (!open && onClose) {
			onClose();
		}
	};

	//Keeps track of what is currently triggering events
	const currentTrigger = useRef(Trigger.Mouse);

	useDidUpdate(() => {
		if (isOpen && firstFocusableInPopoverRef.current) {
			setFocus(firstFocusableInPopoverRef.current, whatTriggered.current);
		} else if (!isOpen && whatTriggered.current == Trigger.Keyboard && buttonRef.current) {
			setFocus(buttonRef.current, whatTriggered.current);
		}
	}, [isOpen]);

	const popoverRef = React.createRef<HTMLDivElement>();

	useOnClickOutside(popoverRef, (e) => {
		//if the click is on the dropdown button we let it handle it instead to avoid double toggeling.
		if (isTargetRef(e.target, buttonRef)) {
			return;
		}
		handleToggle(false, Trigger.Mouse);
	});

	const hasClearSlug = !!onClear && !disabled;

	let buttonId = useUniqueId("dropdown-button");
	buttonId = buttonProps?.id ?? buttonId;
	const clearId = useUniqueId("dropdown-clear");
	return (
		// We need to return a single element and not a button + a 0x0 div
		// since our parent might be display flex
		<div
			data-component={props["data-component"] || "popover"}
			className={cn(
				containerClassName,
				scss.inlineBlock,
				buttonProps && buttonProps.fullWidth && scss.fullWidth,
				hasClearSlug && scss.withClearButton,
				className
			)}
			{...{ style, tabIndex, onKeyDown, onMouseDown, onFocus, onBlur, onMouseEnter, onMouseLeave }}
		>
			<BasePopover
				placement={placement || "bottom-start"}
				allowedAutoPlacements={allowedAutoPlacements}
				showPopover={isOpen}
				strategy={strategy}
				updateKeys={updateKeys}
				anchor={(ref) => {
					const dropdownButton = (
						<DropdownButton
							data-observe-key={props["data-observe-key"]}
							onClick={(e) =>
								handleToggle(!isOpen, isMouseEventKeyboard(e) ? Trigger.Keyboard : Trigger.Mouse)
							}
							onKeyDown={(e) => {
								currentTrigger.current = Trigger.Keyboard;
								// Dropdowns with aria-haspopup should open when pressing Cmd/Ctrl + Arrow Down
								if (ariaHaspopup && (e.ctrlKey || e.metaKey) && e.keyCode === KeyCode.ArrowDown) {
									stopEvent(e);
									handleToggle(true, Trigger.Keyboard);
								}
							}}
							onMouseDown={() => (currentTrigger.current = Trigger.Mouse)}
							isOpen={isOpen}
							//use popover label if open and labelled by popover
							aria-labelledby={labelledByPopover && isOpen ? id : undefined}
							//use aria-label if not labelled by popover and not open
							aria-label={labelledByPopover && isOpen ? undefined : ariaLabel}
							aria-controls={id}
							aria-haspopup={ariaHaspopup}
							ref={combinedRefs<HTMLButtonElement>(buttonRef, ref)}
							hideChevron={hideChevron}
							{...buttonProps}
							id={buttonId}
							className={cn(
								buttonProps?.className,
								!buttonProps?.fullWidth && !noMaxWidth && scss.buttonMaxSize,
								invalid && scss.invalid,
								hasClearSlug && scss.withClearButton
							)}
							disabled={disabled || buttonProps?.disabled}
						>
							{buttonContent}
						</DropdownButton>
					);
					return tooltip ? (
						<Tooltip
							content={tooltip}
							placement={tooltipPlacement}
							variant={{ type: "interactive" }}
						>
							{dropdownButton}
						</Tooltip>
					) : (
						dropdownButton
					);
				}}
				popover={
					<section
						className={cn(
							scss.dropdownBox,
							noMinWidth && scss.noMinWidth,
							noMaxWidth && scss.noMaxWidth
						)}
						ref={popoverRef}
						onKeyDown={(e) => {
							currentTrigger.current = Trigger.Keyboard;
							if (e.keyCode === KeyCode.Escape) {
								stopEvent(e);
								handleToggle(false, Trigger.Keyboard);
							}
						}}
						onMouseDown={() => (currentTrigger.current = Trigger.Mouse)}
						onBlur={(e) => {
							if (currentTrigger.current === Trigger.Keyboard && isBlurNavigatingOut(e)) {
								handleToggle(false, Trigger.Keyboard);
							}
						}}
					>
						{popoverContent(
							id,
							firstFocusableInPopoverRef,
							{ isOpen, setIsOpen, close },
							currentTrigger.current
						)}
					</section>
				}
			/>
			{onClear && !disabled && (
				<Button
					className={cn(scss.clearButton, invalid && scss.invalid, "fancy-DropdownClearButton")}
					id={clearId}
					aria-label={i18n.clearFilter}
					aria-labelledby={cn(clearId, buttonId)}
					size={buttonProps?.size ?? "medium"}
					onClick={onClear}
					data-observe-key={dataObserveKeyDiscriminator(props["data-observe-key"], "ClearButton")}
				>
					<Icon>
						<IconClose />
					</Icon>
				</Button>
			)}
		</div>
	);
}

export type DropdownButtonProps = Pick<
	ButtonProps,
	| "disabled"
	| "className"
	| "size"
	| "variant"
	| "onClick"
	| "onKeyDown"
	| "id"
	| "onBlur"
	| "onMouseDown"
	| "aria-describedby"
	| "aria-label"
	| "aria-labelledby"
	| "aria-controls"
	| "aria-haspopup"
	| "data-observe-key"
	| "style"
> & {
	children?: ReactNode;
	fullWidth?: boolean;
	isOpen?: boolean;
	hideChevron?: boolean;
	chevronColor?: string;
};

export const DropdownButton = React.forwardRef(
	(props: DropdownButtonProps, ref: React.Ref<unknown>) => {
		const {
			children,
			onClick,
			onBlur,
			onMouseDown,
			onKeyDown,
			className,
			isOpen,
			fullWidth,
			hideChevron,
			chevronColor,
			id: propId,
			disabled,
			size,
			style,
			variant,
			"aria-labelledby": ariaLabelledBy,
			"aria-label": ariaLabel,
			"aria-controls": ariaControls,
			"aria-describedby": ariaDescribedBy,
			"aria-haspopup": ariaHaspopup,
		} = props;

		const uid = useUniqueId("dropdown-button");
		const id = propId || uid;

		return (
			<Button
				data-observe-key={props["data-observe-key"]}
				data-component="dropdown-button"
				onClick={onClick}
				onBlur={onBlur}
				onKeyDown={onKeyDown}
				onMouseDown={onMouseDown}
				id={id}
				aria-labelledby={cn(ariaLabelledBy, id)}
				aria-expanded={isOpen}
				aria-controls={ariaControls}
				aria-describedby={ariaDescribedBy}
				aria-haspopup={ariaHaspopup}
				className={cn(scss.dropdownButton, className, "fancy-DropdownButton")}
				active={isOpen}
				style={fullWidth ? { width: "100%", justifyContent: "space-between", ...style } : style}
				ref={ref}
				disabled={disabled}
				size={size}
				variant={variant}
			>
				{ariaLabel && <SrOnly>{ariaLabel}</SrOnly>}
				{children}
				{!hideChevron && (
					<Icon fill={chevronColor} rotation={isOpen ? "180" : "0"}>
						<IconCaret />
					</Icon>
				)}
			</Button>
		);
	}
);

export interface PopoverFooterProps {
	children: React.ReactNode;
}

function PopoverFooter({ children }: PopoverFooterProps): JSX.Element {
	return (
		<div className={scss.dropdownFooter}>
			<Divider />
			{children}
		</div>
	);
}

Popover.Footer = PopoverFooter;
